import { memo, useState, useRef, useEffect, useMemo, useCallback, type KeyboardEvent, type PointerEvent } from 'react'
import { NodeResizer } from '@xyflow/react'
import NodeContainer from './shared/NodeContainer'
import { cn } from '../lib/utils'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import StickyNoteEditor from './StickyNoteEditor'
import StickyNoteSizeControls from './StickyNoteSizeControls'
import {
  STICKY_COLORS,
  STICKY_MIN_WIDTH,
  STICKY_MAX_WIDTH,
  STICKY_MIN_HEIGHT,
  STICKY_MAX_HEIGHT,
} from '../lib/sticky/constants'

// NOTE: highlight.js is loaded from CDN to avoid 950KB bundle bloat
// The CSS is added dynamically when needed
// For now, we skip syntax highlighting and just render code as-is

// Dynamically inject highlight.js CSS when first needed
let cssLoaded = false
function ensureHighlightCssLoaded() {
  if (!cssLoaded) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://cdn.jsdelivr.net/npm/highlight.js@11.11.1/styles/github-dark.min.css'
    document.head.appendChild(link)
    cssLoaded = true
  }
}

// Configure marked for GitHub Flavored Markdown
marked.setOptions({
  gfm: true,
  breaks: true,
})

// Color palette & size bounds are imported from lib/sticky/constants

export interface StickyNoteData {
  label?: string
  text: string
  color: keyof typeof STICKY_COLORS
  width: number
  height: number
  shadow: boolean
  fontSize: number
  onTextChange?: (text: string) => void
  onEditingChange?: (isEditing: boolean) => void
  onSizeChange?: (size: { width: number; height: number; dx?: number; dy?: number }) => void
}

interface StickyNoteProps {
  data: StickyNoteData
  selected?: boolean
}

// Helper: Detect and convert YouTube/Vimeo URLs to embeds
function enhanceVideoLinks(html: string): string {
  // YouTube patterns
  const youtubeRegex = /<a href="https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:[?&][^"]*)?">.*?<\/a>/gi
  html = html.replace(youtubeRegex, (match, videoId) => {
    return `<div class="video-embed"><iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`
  })

  // Vimeo patterns
  const vimeoRegex = /<a href="https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:[?&][^"]*)?">.*?<\/a>/gi
  html = html.replace(vimeoRegex, (match, videoId) => {
    return `<div class="video-embed"><iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`
  })

  return html
}

// Helper: Enhance external links with target="_blank"
function enhanceLinks(html: string): string {
  return html.replace(/<a href="http/gi, '<a target="_blank" rel="noopener noreferrer" href="http')
}

// Helper: Apply basic code styling (without highlight.js for bundle size)
// Syntax highlighting can be added later as a feature
async function applySyntaxHighlighting(html: string): Promise<string> {
  ensureHighlightCssLoaded()
  // For now, we skip actual highlighting to avoid the 950KB highlight.js library
  // Code blocks are still properly formatted with CSS classes
  return html
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w'

const StickyNote = memo(({ data, selected }: StickyNoteProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(data.text || '')
  const [renderedHTML, setRenderedHTML] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: Math.max(STICKY_MIN_WIDTH, Math.min(STICKY_MAX_WIDTH, data.width || 200)),
    height: Math.max(STICKY_MIN_HEIGHT, Math.min(STICKY_MAX_HEIGHT, data.height || 150)),
  })
  const [isResizing, setIsResizing] = useState(false)
  const resizeData = useRef<{ handle: ResizeHandle; startX: number; startY: number; startWidth: number; startHeight: number; startLeft: number; startTop: number } | undefined>(undefined)

  const color = STICKY_COLORS[data.color] || STICKY_COLORS.yellow
  const width = size.width
  const height = size.height
  const fontSize = data.fontSize || 12
  const shadow = data.shadow ?? true

  // Sync internal size state when data.width/height changes from external source (inspector)
  useEffect(() => {
    const newWidth = Math.max(STICKY_MIN_WIDTH, Math.min(STICKY_MAX_WIDTH, data.width || 200))
    const newHeight = Math.max(STICKY_MIN_HEIGHT, Math.min(STICKY_MAX_HEIGHT, data.height || 150))

    // Only update if values actually changed to avoid infinite loops
    if (newWidth !== size.width || newHeight !== size.height) {
      setSize({ width: newWidth, height: newHeight })
    }
  }, [data.width, data.height])

  // Sync editText when data.text changes from external source (inspector) - only if not editing
  useEffect(() => {
    if (!isEditing && data.text !== editText) {
      setEditText(data.text || '')
    }
  }, [data.text, isEditing])

  // Detect theme (light/dark mode)
  const isDarkMode = useMemo(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }
    return false
  }, [])

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [isEditing])

  // Parse markdown with all enhancements - now using useEffect for async highlighting
  useEffect(() => {
    if (!data.text || isEditing) {
      setRenderedHTML('')
      return
    }

    try {
      // 1. Parse markdown to HTML
      let html = marked.parse(data.text) as string

      // 2. Enhance video links
      html = enhanceVideoLinks(html)

      // 3. Enhance external links
      html = enhanceLinks(html)

      // 4. Apply syntax highlighting asynchronously
      applySyntaxHighlighting(html)
        .then((highlightedHtml) => {
          // 5. Sanitize with DOMPurify
          html = DOMPurify.sanitize(highlightedHtml, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
          })
          setRenderedHTML(html)
        })
        .catch((e) => {
          console.error('Error applying syntax highlighting:', e)
          // Fallback: sanitize without highlighting
          html = DOMPurify.sanitize(html, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
          })
          setRenderedHTML(html)
        })
    } catch (e) {
      console.error('Error parsing markdown:', e)
      setRenderedHTML(DOMPurify.sanitize(data.text))
    }
  }, [data.text, isEditing])

  const handleDoubleClick = () => {
    setEditText(data.text || '')
    setIsEditing(true)
    data.onEditingChange?.(true)
  }

  const handleClose = () => {
    setIsEditing(false)
    data.onEditingChange?.(false)
  }

  const handleSave = () => {
    data.onTextChange?.(editText)
    setIsEditing(false)
    data.onEditingChange?.(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      handleClose()
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      const start = e.currentTarget.selectionStart
      const end = e.currentTarget.selectionEnd
      const newText = editText.substring(0, start) + '  ' + editText.substring(end)
      setEditText(newText)

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + 2
        }
      }, 0)
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      const start = e.currentTarget.selectionStart
      const beforeCursor = editText.substring(0, start)
      const afterCursor = editText.substring(e.currentTarget.selectionEnd)

      // Remove up to 2 spaces before cursor
      const match = beforeCursor.match(/( {1,2})$/)
      if (match) {
        const newText = beforeCursor.slice(0, -match[1].length) + afterCursor
        setEditText(newText)
        setTimeout(() => {
          if (textareaRef.current) {
            const newPos = start - match[1].length
            textareaRef.current.selectionStart = textareaRef.current.selectionEnd = newPos
          }
        }, 0)
      }
    }
  }

  const applySize = (next: { width: number; height: number }) => {
    const clamped = {
      width: Math.max(STICKY_MIN_WIDTH, Math.min(STICKY_MAX_WIDTH, Math.round(next.width))),
      height: Math.max(STICKY_MIN_HEIGHT, Math.min(STICKY_MAX_HEIGHT, Math.round(next.height))),
    }
    setSize(clamped)
  }

  const handleResize = (_evt: MouseEvent, params: { width: number; height: number }) => {
    applySize(params)
  }

  const handleResizeEnd = (_evt: MouseEvent, params: { width: number; height: number }) => {
    applySize(params)
    data.onSizeChange?.(params)
  }

  // Manual resize handle interactions
  const handlePointerDown = useCallback((e: PointerEvent, handle: ResizeHandle) => {
    e.preventDefault()
    e.stopPropagation()

    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    setIsResizing(true)
    resizeData.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startLeft: rect.left,
      startTop: rect.top,
    }

    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [size])

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!isResizing || !resizeData.current) return

    const { handle, startX, startY, startWidth, startHeight } = resizeData.current
    const dx = e.clientX - startX
    const dy = e.clientY - startY

    let newWidth = startWidth
    let newHeight = startHeight
    let positionOffset = { dx: 0, dy: 0 }

    switch (handle) {
      case 'se':
        newWidth = startWidth + dx
        newHeight = startHeight + dy
        break
      case 'sw':
        newWidth = startWidth - dx
        newHeight = startHeight + dy
        positionOffset.dx = dx
        break
      case 'ne':
        newWidth = startWidth + dx
        newHeight = startHeight - dy
        positionOffset.dy = dy
        break
      case 'nw':
        newWidth = startWidth - dx
        newHeight = startHeight - dy
        positionOffset.dx = dx
        positionOffset.dy = dy
        break
      case 'e':
        newWidth = startWidth + dx
        break
      case 'w':
        newWidth = startWidth - dx
        positionOffset.dx = dx
        break
      case 's':
        newHeight = startHeight + dy
        break
      case 'n':
        newHeight = startHeight - dy
        positionOffset.dy = dy
        break
    }

    const clamped = {
      width: Math.max(STICKY_MIN_WIDTH, Math.min(STICKY_MAX_WIDTH, newWidth)),
      height: Math.max(STICKY_MIN_HEIGHT, Math.min(STICKY_MAX_HEIGHT, newHeight)),
    }

    setSize(clamped)

    if (positionOffset.dx !== 0 || positionOffset.dy !== 0) {
      data.onSizeChange?.({ ...clamped, ...positionOffset })
    }
  }, [isResizing, data])

  const handlePointerUp = useCallback((e: PointerEvent) => {
    if (!isResizing) return

    setIsResizing(false)
    data.onSizeChange?.(size)

    ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    resizeData.current = undefined
  }, [isResizing, size, data])

  const getHandleCursor = (handle: ResizeHandle): string => {
    const cursors: Record<ResizeHandle, string> = {
      nw: 'nw-resize',
      ne: 'ne-resize',
      sw: 'sw-resize',
      se: 'se-resize',
      n: 'n-resize',
      s: 's-resize',
      e: 'e-resize',
      w: 'w-resize',
    }
    return cursors[handle]
  }

  const renderResizeHandle = (handle: ResizeHandle) => {
    const positions: Record<ResizeHandle, string> = {
      nw: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2',
      ne: 'top-0 right-0 translate-x-1/2 -translate-y-1/2',
      sw: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2',
      se: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2',
      n: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2',
      s: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2',
      e: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2',
      w: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2',
    }

    return (
      <div
        key={handle}
        className={cn(
          'absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 z-20 shadow-md hover:shadow-lg hover:scale-110',
          positions[handle]
        )}
        style={{ cursor: getHandleCursor(handle) }}
        onPointerDown={(e) => handlePointerDown(e, handle)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
    )
  }

  const handleNudge = (dx: number, dy: number) => {
    const next = { width: size.width + dx, height: size.height + dy }
    const clamped = {
      width: Math.max(STICKY_MIN_WIDTH, Math.min(STICKY_MAX_WIDTH, next.width)),
      height: Math.max(STICKY_MIN_HEIGHT, Math.min(STICKY_MAX_HEIGHT, next.height)),
    }
    setSize(clamped)
    data.onSizeChange?.(clamped)
  }

  return (
    <NodeContainer
      variant="default"
      resizable={false}
      sourceFile="nodes/StickyNote.tsx"
      className={cn('rounded-lg transition-all duration-200', shadow && 'shadow-lg hover:shadow-xl', selected && 'ring-2 ring-blue-500')}
    >
      <div
        ref={containerRef}
        className="relative"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: color.light,
          color: color.dark,
        }}
        onDoubleClick={handleDoubleClick}
      >
      <NodeResizer
        minWidth={STICKY_MIN_WIDTH}
        minHeight={STICKY_MIN_HEIGHT}
        maxWidth={STICKY_MAX_WIDTH}
        maxHeight={STICKY_MAX_HEIGHT}
        isVisible={false}
        onResize={handleResize as any}
        onResizeEnd={handleResizeEnd as any}
      />

      {/* Manual resize handles - only show when selected */}
      {selected && !isEditing && (
        <>
          {renderResizeHandle('nw')}
          {renderResizeHandle('ne')}
          {renderResizeHandle('sw')}
          {renderResizeHandle('se')}
          {renderResizeHandle('n')}
          {renderResizeHandle('s')}
          {renderResizeHandle('e')}
          {renderResizeHandle('w')}
        </>
      )}

      {isEditing ? (
        <div className="nodrag">
          <StickyNoteEditor
            fontSize={fontSize}
            text={editText}
            onChange={setEditText}
            onSave={handleSave}
            onClose={handleClose}
            onKeyDown={handleKeyDown}
            ref={textareaRef}
          />
        </div>
      ) : renderedHTML ? (
        <div
          className="sticky-note-content p-3 h-full overflow-auto nowheel"
          style={{ fontSize: `${fontSize}px` }}
          onWheelCapture={(e) => e.stopPropagation()}
          dangerouslySetInnerHTML={{ __html: renderedHTML }}
        />
      ) : (
        <div
          className="sticky-note-content p-3 h-full overflow-auto nowheel"
          style={{ fontSize: `${fontSize}px` }}
          onWheelCapture={(e) => e.stopPropagation()}
        >
          {!data.text && (
            <span className="text-opacity-50">Doble-click para editar...</span>
          )}
        </div>
      )}

        <div className="nodrag">
          <StickyNoteSizeControls
            visible={!!selected && !isEditing}
            onNudge={handleNudge}
          />
        </div>

        {/* Markdown CSS Styling */}
        <style>{`
        .sticky-note-content {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .sticky-note-content h1,
        .sticky-note-content h2,
        .sticky-note-content h3,
        .sticky-note-content h4,
        .sticky-note-content h5,
        .sticky-note-content h6 {
          margin: 0.5em 0 0.25em 0;
          font-weight: 600;
          line-height: 1.25;
        }

        .sticky-note-content h1 { font-size: 1.75em; }
        .sticky-note-content h2 { font-size: 1.5em; }
        .sticky-note-content h3 { font-size: 1.25em; }
        .sticky-note-content h4 { font-size: 1.1em; }
        .sticky-note-content h5 { font-size: 1em; }
        .sticky-note-content h6 { font-size: 0.9em; opacity: 0.8; }

        .sticky-note-content p {
          margin: 0.5em 0;
          line-height: 1.6;
        }

        .sticky-note-content ul,
        .sticky-note-content ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }

        .sticky-note-content li {
          margin: 0.25em 0;
        }

        .sticky-note-content code {
          background: rgba(0, 0, 0, 0.1);
          padding: 0.15em 0.3em;
          border-radius: 3px;
          font-family: 'Courier New', Courier, monospace;
          font-size: 0.9em;
        }

        .sticky-note-content pre {
          background: rgba(0, 0, 0, 0.8);
          color: #f8f8f2;
          padding: 0.75em;
          border-radius: 5px;
          overflow-x: auto;
          margin: 0.75em 0;
        }

        .sticky-note-content pre code {
          background: transparent;
          padding: 0;
          color: inherit;
          font-size: 0.85em;
        }

        .sticky-note-content blockquote {
          border-left: 3px solid currentColor;
          padding-left: 0.75em;
          margin: 0.75em 0;
          opacity: 0.8;
          font-style: italic;
        }

        .sticky-note-content a {
          color: currentColor;
          text-decoration: underline;
          opacity: 0.85;
        }

        .sticky-note-content a:hover {
          opacity: 1;
        }

        .sticky-note-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.75em 0;
          font-size: 0.9em;
        }

        .sticky-note-content th,
        .sticky-note-content td {
          border: 1px solid currentColor;
          padding: 0.4em 0.6em;
          text-align: left;
        }

        .sticky-note-content th {
          background: rgba(0, 0, 0, 0.1);
          font-weight: 600;
        }

        .sticky-note-content img {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          margin: 0.5em 0;
        }

        .sticky-note-content hr {
          border: none;
          border-top: 2px solid currentColor;
          opacity: 0.3;
          margin: 1em 0;
        }

        .sticky-note-content .video-embed {
          position: relative;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          height: 0;
          overflow: hidden;
          margin: 0.75em 0;
          border-radius: 4px;
        }

        .sticky-note-content .video-embed iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 4px;
        }

        .sticky-note-content strong {
          font-weight: 700;
        }

        .sticky-note-content em {
          font-style: italic;
        }

        .sticky-note-content del {
          text-decoration: line-through;
          opacity: 0.7;
        }
        `}</style>
      </div>
    </NodeContainer>
  )
})

StickyNote.displayName = 'StickyNote'

export default StickyNote
